/**
 * House interiors scene — hotspots, popovers, video, Theme Editor helpers.
 * Scoped: [data-house-interiors-scene]
 */
(function () {
  'use strict';

  var instances = new WeakMap();

  function isMobile() {
    return window.matchMedia && window.matchMedia('(max-width: 767.98px)').matches;
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function init(root) {
    if (!root || instances.has(root)) return;

    function closestStage(hotspot) {
      return (hotspot && hotspot.closest('[data-house-interiors-scene-stage]')) || root.querySelector('[data-house-interiors-scene-stage]');
    }

    var sheetRoot = root.parentElement
      ? root.parentElement.querySelector('[data-house-interiors-scene-sheet-root]')
      : null;
    if (!sheetRoot) {
      sheetRoot = document.querySelector(
        '#shopify-section-' + root.getAttribute('data-section-id') + ' [data-house-interiors-scene-sheet-root]'
      );
    }
    /* Sheet is sibling after section in Shopify section wrapper */
    var sectionEl = root.closest('.shopify-section') || root.parentElement;
    if (sectionEl) {
      sheetRoot = sectionEl.querySelector('[data-house-interiors-scene-sheet-root]');
    }

    var openHotspot = null;
    var lastFocus = null;
    var dragState = null;
    var hoverCloseTimer = null;
    var HOVER_CLOSE_MS = 280;

    function canHover() {
      return window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    }

    function cancelHoverClose() {
      if (hoverCloseTimer) {
        clearTimeout(hoverCloseTimer);
        hoverCloseTimer = null;
      }
    }

    function scheduleHoverClose() {
      if (hoverCloseTimer) clearTimeout(hoverCloseTimer);
      hoverCloseTimer = setTimeout(function () {
        hoverCloseTimer = null;
        closeAll();
      }, HOVER_CLOSE_MS);
    }

    function closeAll() {
      cancelHoverClose();
      var hotspots = root.querySelectorAll('[data-house-interiors-scene-hotspot].is-open');
      Array.prototype.forEach.call(hotspots, function (h) {
        h.classList.remove('is-open');
        var btn = h.querySelector('[data-house-interiors-scene-marker]');
        var pop = h.querySelector('[data-house-interiors-scene-popover]');
        if (btn) btn.setAttribute('aria-expanded', 'false');
        if (pop) pop.hidden = true;
      });
      if (sheetRoot) {
        sheetRoot.hidden = true;
        var sheet = sheetRoot.querySelector('[data-house-interiors-scene-sheet]');
        if (sheet) sheet.innerHTML = '';
      }
      openHotspot = null;
      if (lastFocus && lastFocus.focus) {
        try {
          lastFocus.focus();
        } catch (e) {}
      }
      lastFocus = null;
      document.removeEventListener('keydown', onDocKey);
      document.removeEventListener('pointerdown', onDocPointer, true);
    }

    function positionPopover(hotspot, pop) {
      pop.classList.remove('is-left', 'is-right', 'is-above');
      var align = hotspot.getAttribute('data-popover-align') || 'auto';
      var hostStage = closestStage(hotspot);
      if (!hostStage) return;
      var stageRect = hostStage.getBoundingClientRect();
      var hotRect = hotspot.getBoundingClientRect();

      if (align === 'left') pop.classList.add('is-left');
      else if (align === 'right') pop.classList.add('is-right');
      else {
        var spaceRight = stageRect.right - hotRect.right;
        var spaceLeft = hotRect.left - stageRect.left;
        if (spaceRight < 320 && spaceLeft > spaceRight) pop.classList.add('is-left');
        else if (spaceLeft < 320 && spaceRight > spaceLeft) pop.classList.add('is-right');
      }

      pop.hidden = false;
      var popRect = pop.getBoundingClientRect();
      if (popRect.bottom > stageRect.bottom - 8 || popRect.bottom > window.innerHeight - 8) {
        pop.classList.add('is-above');
      }
      if (popRect.left < 8) {
        pop.classList.remove('is-left');
        pop.classList.add('is-right');
      }
      if (popRect.right > window.innerWidth - 8) {
        pop.classList.remove('is-right');
        pop.classList.add('is-left');
      }
    }

    function openHotspotEl(hotspot) {
      var btn = hotspot.querySelector('[data-house-interiors-scene-marker]');
      var pop = hotspot.querySelector('[data-house-interiors-scene-popover]');
      if (!btn || !pop) return;

      cancelHoverClose();
      if (openHotspot === hotspot && hotspot.classList.contains('is-open') && !isMobile()) {
        return;
      }

      closeAll();
      lastFocus = btn;
      openHotspot = hotspot;
      hotspot.classList.add('is-open');
      btn.setAttribute('aria-expanded', 'true');

      if (isMobile() && sheetRoot) {
        var sheet = sheetRoot.querySelector('[data-house-interiors-scene-sheet]');
        var clone = pop.cloneNode(true);
        clone.hidden = false;
        clone.removeAttribute('id');
        clone.classList.remove('is-left', 'is-right', 'is-above');
        clone.style.left = '';
        clone.style.top = '';
        clone.style.right = '';
        clone.style.bottom = '';
        clone.style.transform = '';
        if (sheet) {
          sheet.innerHTML = '';
          sheet.appendChild(clone);
        }
        var stage = closestStage(hotspot);
        if (stage && sheetRoot.parentElement !== stage) {
          stage.appendChild(sheetRoot);
        }
        sheetRoot.hidden = false;
        if (sheet && sheet.focus) sheet.focus();
      } else {
        positionPopover(hotspot, pop);
      }

      document.addEventListener('keydown', onDocKey);
      document.addEventListener('pointerdown', onDocPointer, true);
    }

    function onDocKey(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeAll();
      }
    }

    function onDocPointer(e) {
      if (!openHotspot) return;
      if (isMobile() && sheetRoot && !sheetRoot.hidden) {
        /* Keep the card open while the page scrolls; close via X or hotspot toggle */
        return;
      }
      if (openHotspot.contains(e.target)) return;
      if (sheetRoot && !sheetRoot.hidden && sheetRoot.contains(e.target)) {
        if (e.target.matches('[data-house-interiors-scene-sheet-backdrop]')) closeAll();
        return;
      }
      closeAll();
    }

    function onRootClick(e) {
      var closeBtn = e.target.closest('[data-house-interiors-scene-close]');
      if (closeBtn) {
        e.preventDefault();
        closeAll();
        return;
      }

      var marker = e.target.closest('[data-house-interiors-scene-marker]');
      if (!marker || !root.contains(marker)) return;
      e.preventDefault();
      var hotspot = marker.closest('[data-house-interiors-scene-hotspot]');
      if (!hotspot) return;
      if (canHover() && !isMobile()) {
        if (hotspot.classList.contains('is-open')) closeAll();
        else openHotspotEl(hotspot);
        return;
      }
      if (hotspot.classList.contains('is-open')) closeAll();
      else openHotspotEl(hotspot);
    }

    function bindHotspotHover(hotspot) {
      if (hotspot.getAttribute('data-his-hover-bound') === 'true') return;
      hotspot.setAttribute('data-his-hover-bound', 'true');

      function onEnter() {
        if (!canHover() || isMobile()) return;
        cancelHoverClose();
        openHotspotEl(hotspot);
      }

      function onLeave(e) {
        if (!canHover() || isMobile()) return;
        var next = e.relatedTarget;
        if (next && hotspot.contains(next)) return;
        scheduleHoverClose();
      }

      hotspot.addEventListener('mouseenter', onEnter);
      hotspot.addEventListener('mouseleave', onLeave);
      var pop = hotspot.querySelector('[data-house-interiors-scene-popover]');
      if (pop) {
        pop.addEventListener('mouseenter', onEnter);
        pop.addEventListener('mouseleave', onLeave);
      }
    }

    Array.prototype.forEach.call(root.querySelectorAll('[data-house-interiors-scene-hotspot]'), bindHotspotHover);

    /* Video */
    var video = root.querySelector('.house-interiors-scene__video');
    var videoToggle = root.querySelector('[data-house-interiors-scene-video-toggle]');
    var videoObserver = null;

    function syncVideoToggle() {
      if (!video || !videoToggle) return;
      var playing = !video.paused;
      videoToggle.setAttribute('aria-pressed', playing ? 'true' : 'false');
      videoToggle.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
    }

    function onVideoToggle(e) {
      e.preventDefault();
      if (!video) return;
      if (video.paused) {
        var p = video.play();
        if (p && p.catch) p.catch(function () {});
      } else {
        video.pause();
      }
      syncVideoToggle();
    }

    if (video) {
      if (prefersReducedMotion()) {
        video.removeAttribute('autoplay');
        video.pause();
      }
      if (videoToggle) {
        videoToggle.addEventListener('click', onVideoToggle);
        video.addEventListener('play', syncVideoToggle);
        video.addEventListener('pause', syncVideoToggle);
        syncVideoToggle();
      }
      if ('IntersectionObserver' in window) {
        videoObserver = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting && video && !video.paused) video.pause();
            });
          },
          { threshold: 0.15 }
        );
        videoObserver.observe(video);
      }
    }

    /* Design mode drag — preview only; does not persist settings */
    function onMarkerPointerDown(e) {
      if (!window.Shopify || !Shopify.designMode) return;
      if (e.button !== 0) return;
      var marker = e.target.closest('[data-house-interiors-scene-marker]');
      if (!marker) return;
      var hotspot = marker.closest('[data-house-interiors-scene-hotspot]');
      if (!hotspot || !closestStage(hotspot)) return;
      e.preventDefault();
      e.stopPropagation();
      dragState = {
        hotspot: hotspot,
        stage: closestStage(hotspot),
        pointerId: e.pointerId
      };
      try {
        marker.setPointerCapture(e.pointerId);
      } catch (err) {}
      var hint = root.querySelector('[data-house-interiors-scene-editor-hint]');
      if (hint) hint.hidden = false;
    }

    function onMarkerPointerMove(e) {
      if (!dragState || !dragState.stage) return;
      var rect = dragState.stage.getBoundingClientRect();
      var x = ((e.clientX - rect.left) / rect.width) * 100;
      var y = ((e.clientY - rect.top) / rect.height) * 100;
      x = Math.max(0, Math.min(100, x));
      y = Math.max(0, Math.min(100, y));
      var mobile = isMobile();
      if (mobile) {
        dragState.hotspot.style.setProperty('--his-x-m', x.toFixed(0) + '%');
        dragState.hotspot.style.setProperty('--his-y-m', y.toFixed(0) + '%');
      } else {
        dragState.hotspot.style.setProperty('--his-x-d', x.toFixed(0) + '%');
        dragState.hotspot.style.setProperty('--his-y-d', y.toFixed(0) + '%');
      }
      var coords = dragState.hotspot.querySelector('[data-house-interiors-scene-coords]');
      if (coords) {
        var dx = parseFloat(getComputedStyle(dragState.hotspot).getPropertyValue('--his-x-d')) || x;
        var dy = parseFloat(getComputedStyle(dragState.hotspot).getPropertyValue('--his-y-d')) || y;
        var mx = parseFloat(getComputedStyle(dragState.hotspot).getPropertyValue('--his-x-m')) || x;
        var my = parseFloat(getComputedStyle(dragState.hotspot).getPropertyValue('--his-y-m')) || y;
        coords.textContent =
          'Desktop ' +
          Math.round(dx) +
          '% / ' +
          Math.round(dy) +
          '% · Mobile ' +
          Math.round(mx) +
          '% / ' +
          Math.round(my) +
          '% — copy into block settings';
      }
    }

    function onMarkerPointerUp() {
      dragState = null;
    }

    root.addEventListener('click', onRootClick);
    root.addEventListener('pointerdown', onMarkerPointerDown, true);
    root.addEventListener('pointermove', onMarkerPointerMove, true);
    root.addEventListener('pointerup', onMarkerPointerUp, true);
    root.addEventListener('pointercancel', onMarkerPointerUp, true);

    if (sheetRoot) {
      sheetRoot.addEventListener('click', function (e) {
        if (e.target.closest('[data-house-interiors-scene-close]') || e.target.matches('[data-house-interiors-scene-sheet-backdrop]')) {
          closeAll();
        }
      });
    }

    function onBlockSelect(ev) {
      if (!root.contains(ev.target) && ev.target !== root) {
        /* event fires on section; detail has blockId */
      }
      var blockId = ev.detail && ev.detail.blockId;
      if (!blockId) return;
      var hotspot = root.querySelector('[data-hotspot-id="' + blockId + '"]');
      if (!hotspot) return;
      root.querySelectorAll('.is-editor-selected').forEach(function (el) {
        el.classList.remove('is-editor-selected');
      });
      hotspot.classList.add('is-editor-selected');
      hotspot.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      if (!isMobile()) openHotspotEl(hotspot);
    }

    function onBlockDeselect() {
      root.querySelectorAll('.is-editor-selected').forEach(function (el) {
        el.classList.remove('is-editor-selected');
      });
      closeAll();
    }

    document.addEventListener('shopify:block:select', onBlockSelect);
    document.addEventListener('shopify:block:deselect', onBlockDeselect);

    /* Mobile swipe — native overflow like the dynamic product carousel */
    var track = root.querySelector('[data-his-carousel-track]');
    var scrollBar = root.querySelector('[data-his-scroll-bar]');
    var thumb = root.querySelector('[data-his-scroll-thumb]');
    var carouselDrag = {
      down: false,
      moved: false,
      suppressClick: false,
      startX: 0,
      startY: 0,
      startScroll: 0,
      pointerType: ''
    };

    var swipeHint = root.querySelector('[data-his-swipe-hint]');

    function updateSwipeHint() {
      if (!swipeHint) return;
      if (!isMobile() || !track || track.scrollLeft > 16) {
        swipeHint.hidden = true;
        return;
      }
      swipeHint.hidden = false;
    }

    function updateCarouselScrollBar() {
      updateSwipeHint();
      if (!track || !scrollBar || !thumb) return;
      if (!isMobile()) {
        scrollBar.hidden = true;
        return;
      }
      var maxScroll = track.scrollWidth - track.clientWidth;
      if (maxScroll <= 4) {
        scrollBar.hidden = true;
        thumb.style.transform = 'translateX(0)';
        return;
      }
      scrollBar.hidden = false;
      var maxOffset = Math.max(0, scrollBar.clientWidth - thumb.clientWidth);
      var ratio = track.scrollLeft / maxScroll;
      thumb.style.transform = 'translateX(' + ratio * maxOffset + 'px)';
    }

    function onCarouselPointerDown(e) {
      if (!track || !isMobile()) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      carouselDrag.moved = false;
      carouselDrag.suppressClick = false;
      /* Story/CTA taps must not start a swipe — especially the first slide */
      if (
        e.target &&
        e.target.closest(
          '[data-house-interiors-scene-hotspot], button, input, .house-interiors-scene__story, .house-interiors-scene__story-cta'
        )
      ) {
        carouselDrag.down = false;
        return;
      }
      carouselDrag.down = true;
      carouselDrag.pointerType = e.pointerType || '';
      carouselDrag.startX = e.clientX;
      carouselDrag.startY = e.clientY;
      carouselDrag.startScroll = track.scrollLeft;
      /* Mouse only: grab-drag. Touch uses native momentum scrolling. */
      if (e.pointerType === 'mouse') {
        track.classList.add('is-dragging');
        try {
          track.setPointerCapture(e.pointerId);
        } catch (err) {}
      }
    }

    function onCarouselPointerMove(e) {
      if (!carouselDrag.down || !track) return;
      var dx = e.clientX - carouselDrag.startX;
      var dy = e.clientY - carouselDrag.startY;
      var swipe = Math.abs(dx) > 12 && Math.abs(dx) > Math.abs(dy);
      if (carouselDrag.pointerType !== 'mouse') {
        if (swipe || Math.abs(track.scrollLeft - carouselDrag.startScroll) > 12) {
          carouselDrag.moved = true;
        }
        return;
      }
      if (swipe) carouselDrag.moved = true;
      if (carouselDrag.moved) {
        track.scrollLeft = carouselDrag.startScroll - dx * 1.5;
      }
    }

    function onCarouselPointerUp() {
      if (!carouselDrag.down) return;
      carouselDrag.suppressClick = carouselDrag.moved;
      carouselDrag.down = false;
      carouselDrag.moved = false;
      carouselDrag.pointerType = '';
      if (track) track.classList.remove('is-dragging');
    }

    function onCarouselClickCapture(e) {
      if (!carouselDrag.suppressClick) return;
      carouselDrag.suppressClick = false;
      if (e.target && e.target.closest('.house-interiors-scene__story, .house-interiors-scene__story-cta')) {
        return;
      }
      if (e.target && e.target.closest('a')) {
        e.preventDefault();
        e.stopPropagation();
      }
    }

    function onCarouselNativeScroll() {
      if (carouselDrag.down && Math.abs(track.scrollLeft - carouselDrag.startScroll) > 12) {
        carouselDrag.moved = true;
      }
      updateCarouselScrollBar();
    }

    if (track) {
      track.addEventListener('scroll', onCarouselNativeScroll, { passive: true });
      track.addEventListener('pointerdown', onCarouselPointerDown);
      track.addEventListener('pointermove', onCarouselPointerMove);
      track.addEventListener('pointerup', onCarouselPointerUp);
      track.addEventListener('pointercancel', onCarouselPointerUp);
      track.addEventListener('click', onCarouselClickCapture, true);
      window.addEventListener('resize', updateCarouselScrollBar);
      updateCarouselScrollBar();
      requestAnimationFrame(updateCarouselScrollBar);
    }

    instances.set(root, {
      destroy: function () {
        closeAll();
        root.removeEventListener('click', onRootClick);
        root.removeEventListener('pointerdown', onMarkerPointerDown, true);
        root.removeEventListener('pointermove', onMarkerPointerMove, true);
        root.removeEventListener('pointerup', onMarkerPointerUp, true);
        root.removeEventListener('pointercancel', onMarkerPointerUp, true);
        document.removeEventListener('shopify:block:select', onBlockSelect);
        document.removeEventListener('shopify:block:deselect', onBlockDeselect);
        if (track) {
          track.removeEventListener('scroll', onCarouselNativeScroll);
          track.removeEventListener('pointerdown', onCarouselPointerDown);
          track.removeEventListener('pointermove', onCarouselPointerMove);
          track.removeEventListener('pointerup', onCarouselPointerUp);
          track.removeEventListener('pointercancel', onCarouselPointerUp);
          track.removeEventListener('click', onCarouselClickCapture, true);
        }
        window.removeEventListener('resize', updateCarouselScrollBar);
        if (videoToggle) videoToggle.removeEventListener('click', onVideoToggle);
        if (videoObserver) videoObserver.disconnect();
      }
    });
  }

  function destroy(root) {
    var inst = instances.get(root);
    if (inst && inst.destroy) inst.destroy();
    instances.delete(root);
  }

  function boot(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-house-interiors-scene]'), init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot();
    });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target) boot(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    if (!e.target) return;
    var root = e.target.querySelector('[data-house-interiors-scene]');
    if (root) destroy(root);
  });
})();
